import { AuthContext } from '@repo-level/core-front';
import type { ICentrifugoSubscription } from 'api/notificationCenter';
import { centrifugoSubscription } from 'api/notificationCenter';
import { UnauthorizedError } from 'centrifuge';
import type { FC } from 'react';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { CentrifugoContext } from './context';
import { centrifugoService } from './service';
import { ConnectionStatus } from './types';

export const CentrifugoContextProvider: FC = ({ children }) => {
  const { isAuthenticated, pendingUser } = useContext(AuthContext);

  const [settings, setSettings] = useState<ICentrifugoSubscription | null>(
    null
  );

  const [status, setStatus] = useState(ConnectionStatus.Disconnected);

  // pendingUser flips on every fetchUser() — deposit, profile save, 2FA. Latching it keeps
  // those from refetching settings: a fresh JWT would change `token` and force a reconnect.
  const [authResolved, setAuthResolved] = useState(false);

  useEffect(() => {
    if (!pendingUser) setAuthResolved(true);
  }, [pendingUser]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: isAuthenticated is the refetch trigger on login/logout
  useEffect(() => {
    if (!authResolved) return;

    let cancelled = false;

    (async () => {
      try {
        const { data } = await centrifugoSubscription();

        if (!cancelled) setSettings(data ?? null);
      } catch (error) {
        console.error('[Centrifugo] failed to fetch subscription', error);

        if (!cancelled) setSettings(null);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [authResolved, isAuthenticated]);

  // Goes to the network directly instead of updating `settings`, so a token refresh does not trigger a full reconnect
  const getToken = useCallback(async () => {
    try {
      const { data } = await centrifugoSubscription();

      if (!data?.token) {
        throw new UnauthorizedError(
          'Centrifugo token refresh returned no token'
        );
      }

      return data.token;
    } catch (error) {
      const httpStatus = (error as { status?: number })?.status;

      if (
        typeof httpStatus === 'number' &&
        httpStatus >= 400 &&
        httpStatus < 500
      ) {
        throw new UnauthorizedError(
          `Centrifugo token refresh rejected: ${httpStatus}`
        );
      }

      throw error;
    }
  }, []);

  const url = settings?.url;
  const token = settings?.token;

  useEffect(() => {
    if (!(url && token)) return;

    centrifugoService.connect(url, token, {
      getToken,
      onConnected: () => setStatus(ConnectionStatus.Connected),
      onDisconnected: () => setStatus(ConnectionStatus.Disconnected),
    });

    return () => {
      centrifugoService.disconnect();
      setStatus(ConnectionStatus.Disconnected);
    };
  }, [url, token, getToken]);

  const value = useMemo(() => ({ settings, status }), [settings, status]);

  return (
    <CentrifugoContext.Provider value={value}>
      {children}
    </CentrifugoContext.Provider>
  );
};
